import { appColors } from '@base/useTheme';
import { SheetRowRepresentation, TableTileRowStyle } from '@common/api';
import { Button } from '@components/button';
import { Edit, BoldStyle, ItalicStyle, UnderlineStyle } from '@components/icons/FarseerIcons';
import { GridLayoutIgnoreDrag } from '@components/grid-layout';
import { Divider, DividerVertical } from '@components/layout/Divider';
import { FlexCol, FlexGrow, FlexRow } from '@components/layout/Flex';
import { Popover } from '@components/popover/Popover';
import { FormField, NumberInput, SwitchField } from '@components/form';
import { Toggle } from '@components/toggle/Toggle';
import { TooltipWrapper } from '@components/tooltip/Tooltip';
import { Text } from '@components/typography/Typography';
import { pathToIds } from '@data/sheetRow/sheetRowPathHelpers';
import { ColorPicker } from '@features/colors/ColorPicker';
import { FormattingOverride } from '@features/formatting/FormattingOverride';
import { useI18nContext } from '@i18n/i18n-react';
import { $delete, $set, $patch, $nop, Update, patch } from '@use-gpu/state';
import { DEFAULT_NUMBER_FORMAT } from '@util/enums';
import { addMapListValue } from '@util/multimap';
import { $toggle } from '@util/patch';

export const DEFAULT_ROW_STYLE: Required<TableTileRowStyle> = {
    total: false,

    bold: false,
    italic: false,
    underline: false,

    textColor: '#000000',
    textSize: 12,

    backgroundColor: appColors.neutral.w100,
    borderTopColor: appColors.neutral.w200,
    borderInnerColor: appColors.neutral.w200,
    borderBottomColor: appColors.neutral.w200,

    showBackground: false,
    showBorderTop: false,
    showBorderInner: false,
    showBorderBottom: false,

    numberFormat: { ...DEFAULT_NUMBER_FORMAT },
    height: 0,
};

export type UpdateRowStyle = Update<Record<string, TableTileRowStyle> | undefined>;

type TableTileRowEditProps = {
    rowKey: string;
    prevRowKey?: string;
    nextRowKey?: string;
    isGlobalStyle?: boolean;

    defaultStyle: Required<TableTileRowStyle>;
    rowStyles: Record<string, TableTileRowStyle>;

    updateRowStyles: (rowStyles: Update<Record<string, TableTileRowStyle> | undefined>) => void;
};

export const rowPathToRowKey = (path: string) => pathToIds(path).join('_');

export const getRowGroups = (rows: SheetRowRepresentation[]) => {
    const groups = new Map();
    const keys = new Set();

    for (const row of rows) {
        const rowKey = rowPathToRowKey(row.path);
        keys.add(rowKey);
        addMapListValue(groups, rowKey, row);
    }

    return [...keys.keys()].map((k) => groups.get(k)!);
};

export const TableTileRowEdit = (props: TableTileRowEditProps) => {
    const { rowKey, prevRowKey, nextRowKey, isGlobalStyle, defaultStyle, rowStyles, updateRowStyles } = props;

    const rowStyle = patch(defaultStyle, rowStyles[rowKey]) as Required<TableTileRowStyle>;

    const {
        bold,
        italic,
        underline,
        textColor,
        textSize,
        backgroundColor,
        borderTopColor,
        borderInnerColor,
        borderBottomColor,
        showBackground,
        showBorderTop,
        showBorderInner,
        showBorderBottom,
    } = rowStyle;

    const { LL } = useI18nContext();

    const updateRowStyle = (update: Update<Required<TableTileRowStyle>>) => {
        updateRowStyles({ [rowKey]: update as Update<TableTileRowStyle> });
    };

    const updateRowPrevStyle = (
        update: Update<Required<TableTileRowStyle>>,
        prev: Update<Required<TableTileRowStyle>>
    ) => {
        if (!prevRowKey) return updateRowStyle(update);
        updateRowStyles({
            [rowKey]: update,
            [prevRowKey]: prev,
        } as UpdateRowStyle);
    };

    const updateRowNextStyle = (
        update: Update<Required<TableTileRowStyle>>,
        next: Update<Required<TableTileRowStyle>>
    ) => {
        if (!nextRowKey) return updateRowStyle(update);
        updateRowStyles({
            [rowKey]: update,
            [nextRowKey]: next,
        } as UpdateRowStyle);
    };

    const handleReset = () => {
        const update = {
            [rowKey]: $delete(),
        };
        // Remove bottom border of previous group
        if (prevRowKey) {
            update[prevRowKey] = $patch((style?: TableTileRowStyle) => {
                if (!style) return $nop();

                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { showBorderBottom, borderBottomColor, ...rest } = style;
                if (JSON.stringify(rest).length > 2) return $set(rest);
                return $delete();
            });
        }
        // Remove top border of next group
        if (nextRowKey) {
            update[nextRowKey] = $patch((style?: TableTileRowStyle) => {
                if (!style) return $nop();

                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { showBorderTop, borderTopColor, ...rest } = style;
                if (JSON.stringify(rest).length > 2) return $set(rest);
                return $delete();
            });
        }
        updateRowStyles(update);
    };

    const canReset = !isGlobalStyle && !!rowStyles[rowKey];

    const rowEdit = (
        <FlexCol $gap={8}>
            {!isGlobalStyle ? (
                <FlexRow $align="center">
                    <Text>{LL.CHART_ROW_OPTIONS()}</Text>
                    <FlexGrow />
                    {canReset ? (
                        <Button $type="tertiary" $size="small" title={LL.RESET_STYLES()} onClick={handleReset} />
                    ) : (
                        <div style={{ height: 24 }} />
                    )}
                </FlexRow>
            ) : null}

            <FlexCol>
                <FlexCol $gap={8}>
                    <FormField label={LL.TEXT()}>
                        <FlexRow $align="center" style={{ minHeight: 32, width: '100%' }}>
                            <NumberInput
                                type="int"
                                size="small"
                                value={textSize ?? 12}
                                min={6}
                                max={144}
                                onChange={(textSize) => {
                                    updateRowStyle({ textSize });
                                }}
                                width={60}
                            />

                            <DividerVertical />

                            <TooltipWrapper label={LL.BOLD()}>
                                <Toggle
                                    $size="small"
                                    icon={BoldStyle}
                                    checked={bold}
                                    onChange={() => updateRowStyle({ bold: $toggle() })}
                                />
                            </TooltipWrapper>
                            <TooltipWrapper label={LL.ITALIC()}>
                                <Toggle
                                    $size="small"
                                    icon={ItalicStyle}
                                    checked={italic}
                                    onChange={() => updateRowStyle({ italic: $toggle() })}
                                />
                            </TooltipWrapper>
                            <TooltipWrapper label={LL.UNDERLINED()}>
                                <Toggle
                                    $size="small"
                                    icon={UnderlineStyle}
                                    checked={underline}
                                    onChange={() => updateRowStyle({ underline: $toggle() })}
                                />
                            </TooltipWrapper>

                            <FlexGrow />

                            <ColorPicker value={textColor} onChange={(textColor) => updateRowStyle({ textColor })} />
                        </FlexRow>
                    </FormField>

                    {!isGlobalStyle ? <Divider /> : <div />}
                </FlexCol>

                <SwitchField
                    label={LL.BACKGROUND()}
                    value={showBackground}
                    onChange={() => updateRowStyle({ showBackground: $toggle() })}
                    suffix={
                        <ColorPicker
                            value={backgroundColor}
                            onChange={(backgroundColor) =>
                                updateRowStyle({
                                    showBackground: true,
                                    backgroundColor,
                                })
                            }
                        />
                    }
                />
                {isGlobalStyle ? (
                    <SwitchField
                        label={LL.BORDER()}
                        value={showBorderTop}
                        onChange={() => {
                            updateRowStyle({
                                showBorderTop: !showBorderTop,
                                showBorderInner: !showBorderInner,
                                showBorderBottom: !showBorderTop,
                            });
                        }}
                        suffix={
                            <ColorPicker
                                value={borderTopColor}
                                onChange={(borderTopColor) => {
                                    updateRowStyle({
                                        showBorderTop: true,
                                        showBorderInner: true,
                                        showBorderBottom: true,
                                        borderTopColor,
                                        borderInnerColor: borderTopColor,
                                        borderBottomColor: borderTopColor,
                                    });
                                }}
                            />
                        }
                    />
                ) : (
                    <>
                        <SwitchField
                            label={LL.TOP_BORDER()}
                            value={showBorderTop}
                            onChange={() => {
                                updateRowPrevStyle(
                                    { showBorderTop: !showBorderTop },
                                    { showBorderBottom: !showBorderTop, borderBottomColor: borderTopColor }
                                );
                            }}
                            suffix={
                                <ColorPicker
                                    value={borderTopColor}
                                    onChange={(borderTopColor) => {
                                        updateRowPrevStyle(
                                            {
                                                showBorderTop: true,
                                                borderTopColor,
                                            },
                                            {
                                                showBorderBottom: true,
                                                borderBottomColor: borderTopColor,
                                            }
                                        );
                                    }}
                                />
                            }
                        />
                        <SwitchField
                            label={LL.INNER_BORDER()}
                            value={showBorderInner}
                            onChange={() => {
                                updateRowStyle({
                                    showBorderInner: !showBorderInner,
                                });
                            }}
                            suffix={
                                <ColorPicker
                                    value={borderInnerColor}
                                    onChange={(borderInnerColor) => {
                                        updateRowStyle({
                                            showBorderInner: true,
                                            borderInnerColor,
                                        });
                                    }}
                                />
                            }
                        />
                        <SwitchField
                            label={LL.BOTTOM_BORDER()}
                            value={showBorderBottom}
                            onChange={() => {
                                updateRowNextStyle(
                                    { showBorderBottom: !showBorderBottom },
                                    { showBorderTop: !showBorderBottom, borderTopColor: borderBottomColor }
                                );
                            }}
                            suffix={
                                <ColorPicker
                                    value={borderBottomColor}
                                    onChange={(borderBottomColor) => {
                                        updateRowNextStyle(
                                            {
                                                showBorderBottom: true,
                                                borderBottomColor,
                                            },
                                            {
                                                showBorderTop: true,
                                                borderTopColor: borderBottomColor,
                                            }
                                        );
                                    }}
                                />
                            }
                        />
                    </>
                )}
                <Divider />
                <FormattingOverride
                    hasPolarity={!isGlobalStyle}
                    size="small"
                    numberFormat={rowStyles[rowKey]?.numberFormat ?? null}
                    onChange={(numberFormat) => {
                        updateRowStyle({ numberFormat: numberFormat ?? $delete() });
                    }}
                />
            </FlexCol>
        </FlexCol>
    );

    if (isGlobalStyle) return <div>{rowEdit}</div>;

    return (
        <>
            <Popover.Trigger anchor={false}>
                <div>
                    <Edit color={rowStyle.textColor} />
                </div>
            </Popover.Trigger>
            <Popover.Content width={240} $padding="7px" $small noFocus>
                <GridLayoutIgnoreDrag>
                    <div style={{ padding: 1 }} onClick={(e) => e.stopPropagation()}>
                        {rowEdit}
                    </div>
                </GridLayoutIgnoreDrag>
            </Popover.Content>
        </>
    );
};
